(() => {
  if (window.__faDeliveryBootstrapped) return;
  window.__faDeliveryBootstrapped = true;

  const PRIMARY_API = 'https://api.floweradvisor.com/api';
  const FALLBACK_API = 'https://admin.floweradvisor.net/api';
  const SESSION = {
    city: 'selected_city',
    date: 'selected_date',
    timeslot: 'selected_timeslot',
    specific: 'selected_specific_timeslot'
  };

  const SPECIFIC_TIMING_VARIANTS = Object.freeze({});

  const localISODate = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const cityKey = (name = '') => String(name).replace(/\s+/g, '_').toLowerCase();
  const clearCutoffTime = () => sessionStorage.removeItem('cutoffTime');
  const setCutoffTime = (value) => {
    const cutoff = String(value ?? '').trim();
    if (cutoff) sessionStorage.setItem('cutoffTime', cutoff);
    else clearCutoffTime();
  };

  const formatMYR = (value) => {
    const amount = Number(value || 0);
    if (!Number.isFinite(amount) || amount === 0) return 'Free';
    return `${amount.toLocaleString('en-MY', { maximumFractionDigits: 2 })} MYR`;
  };

  const formatDeliveryDate = (value = '') => {
    if (!value) return '';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  function apiMessage(data) {
    const candidates = [
      data?.message,
      data?.error_message,
      data?.error?.message,
      data?.errors?.message,
      typeof data?.error === 'string' ? data.error : ''
    ];
    return String(candidates.find((item) => item != null && String(item).trim()) || '').trim();
  }

  function shopperSafeReason(error) {
    const raw = String(error?.shopperMessage || error?.message || '').replace(/\s+/g, ' ').trim();
    if (!raw) return '';
    if (/^(HTTP \d+|Failed to fetch|NetworkError|Load failed|TypeError|Internal Server Error|Bad Gateway|Service Unavailable|Gateway Timeout)$/i.test(raw)) return '';
    if (!/(delivery|date|time|slot|cut.?off|closed|unavailable|available|advance|workshop|same.?day|minimum|order)/i.test(raw)) return '';
    return raw.replace(/[<>]/g, '').slice(0, 180);
  }

  async function requestJson(url, timeoutMs = 3500) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
    let response;
    try {
      response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
    } catch (error) {
      if (error?.name === 'AbortError') {
        const timeoutError = new Error('Delivery service timed out');
        timeoutError.code = 'FA_API_TIMEOUT';
        throw timeoutError;
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }

    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      data = null;
    }

    const message = apiMessage(data);
    if (!response.ok) {
      const error = new Error(message || `HTTP ${response.status}`);
      error.status = response.status;
      error.payload = data;
      throw error;
    }

    if (data && (data.error === 1 || data.error === true)) {
      const error = new Error(message || 'Delivery service could not return available times');
      error.payload = data;
      throw error;
    }

    return data;
  }

  async function requestWithFallback(primaryUrl, fallbackUrl) {
    try {
      return await requestJson(primaryUrl);
    } catch (primaryError) {
      console.warn('[FlowerAdvisor] Primary API failed, using fallback.', primaryError);
      try {
        return await requestJson(fallbackUrl);
      } catch (fallbackError) {
        const fallbackReason = shopperSafeReason(fallbackError);
        const primaryReason = shopperSafeReason(primaryError);
        if (!fallbackReason && primaryReason) fallbackError.shopperMessage = primaryReason;
        throw fallbackError;
      }
    }
  }

  function specificTimingKey(slot) {
    const text = String(slot?.text || slot?.label || '');
    const match = text.match(/Specific\s*Timing\s*(\d+)/i);
    return match ? match[1] : null;
  }

  function hasConfiguredSpecificCharge(slot) {
    if (String(slot?.is_specific_timing) !== '1') return true;
    const key = specificTimingKey(slot);
    return Boolean(key && SPECIFIC_TIMING_VARIANTS[key]);
  }

  function initWidget(widget) {
    if (!widget || widget.dataset.faInitialized === 'true') return;
    widget.dataset.faInitialized = 'true';

    const form = widget.closest('form[action*="/cart/add"]');
    if (!form) return;

    const skuInput = widget.querySelector('[data-fa-sku]');
    const variantInput = form.querySelector('.product-variant-id, input[name="id"]');
    const skuMapNode = widget.querySelector('[data-fa-variant-skus]');
    const citySelect = widget.querySelector('[data-fa-city]');
    const datePicker = widget.querySelector('[data-fa-date-picker]');
    const timeslotSelect = widget.querySelector('[data-fa-timeslot-select]');
    const specificWrap = widget.querySelector('[data-fa-specific-wrap]');
    const specificSelect = widget.querySelector('[data-fa-specific-select]');
    const hiddenDate = widget.querySelector('[data-fa-delivery-date]');
    const hiddenTimeslot = widget.querySelector('[data-fa-timeslot]');
    const hiddenTimeslotText = widget.querySelector('[data-fa-timeslot-text]');
    const hiddenSpecific = widget.querySelector('[data-fa-specific-timeslot]');
    const status = widget.querySelector('[data-fa-status]');
    const changeDateButton = widget.querySelector('[data-fa-change-date]');
    const countryCode = widget.dataset.countryCode || 'MY';
    const domainAlias = widget.dataset.domainAlias || 'fa.com.my';
    const productId = widget.dataset.productId || '';
    let skuMap = {};
    let lastVariantId = '';
    let currentSlots = [];
    let cityRequestSeq = 0;
    let timeslotRequestSeq = 0;
    let humanActivated = false;

    try { skuMap = JSON.parse(skuMapNode?.textContent || '{}'); } catch (_) { skuMap = {}; }

    if (datePicker) datePicker.min = localISODate();

    const setStatus = (message = '', state = '') => {
      if (!status) return;
      status.textContent = message;
      if (state) status.dataset.state = state;
      else delete status.dataset.state;
    };

    const showChangeDate = (show) => {
      if (changeDateButton) changeDateButton.hidden = !show;
    };

    const selectedCityName = () => {
      const option = citySelect?.options?.[citySelect.selectedIndex];
      return option?.dataset.cityName || String(option?.textContent || '').split(' - ')[0].trim() || 'this delivery area';
    };

    function clearLegacyDeliveryLocks() {
      form.querySelectorAll('button[name="add"], input[type="submit"][name="add"]').forEach((button) => {
        if (button.dataset.faDeliveryLock === '1') {
          if (!variantInput?.disabled) button.disabled = false;
          delete button.dataset.faDeliveryLock;
        }
      });
    }

    function resetTimeslot(message = 'Choose a date to see available times') {
      currentSlots = [];
      clearCutoffTime();
      if (timeslotSelect) {
        timeslotSelect.innerHTML = `<option value="">${message}</option>`;
        timeslotSelect.disabled = true;
      }
      if (hiddenTimeslot) hiddenTimeslot.value = '';
      if (hiddenTimeslotText) hiddenTimeslotText.value = '';
      if (specificWrap) specificWrap.hidden = true;
      if (specificSelect) specificSelect.innerHTML = '<option value="">Select specific time</option>';
      if (hiddenSpecific) hiddenSpecific.value = '';
    }

    function renderSpecificTimes(slot) {
      if (!specificSelect || !specificWrap || !hiddenSpecific) return;
      specificSelect.innerHTML = '<option value="">Select specific time</option>';
      hiddenSpecific.value = '';
      const startRaw = Number.parseInt(slot.start_time, 10);
      const endRaw = Number.parseInt(slot.end_time, 10);
      if (!Number.isFinite(startRaw) || !Number.isFinite(endRaw) || endRaw <= startRaw) {
        specificWrap.hidden = true;
        return;
      }

      let start = startRaw;
      const nowPlusFour = new Date();
      nowPlusFour.setHours(nowPlusFour.getHours() + 4);
      if (hiddenDate?.value === localISODate(nowPlusFour)) {
        start = Math.max(start, nowPlusFour.getHours());
      }

      for (let hour = start; hour < endRaw; hour += 1) {
        const next = hour + 1;
        const value = `${String(hour).padStart(2, '0')}:00 - ${String(next).padStart(2, '0')}:00`;
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        specificSelect.appendChild(option);
      }
      specificWrap.hidden = false;
    }

    async function loadTimeslots({ restore = false } = {}) {
      const requestSeq = ++timeslotRequestSeq;
      if (!citySelect?.value || !hiddenDate?.value) {
        resetTimeslot();
        showChangeDate(false);
        return;
      }

      resetTimeslot('Loading available times...');
      showChangeDate(false);
      setStatus('Checking delivery availability...');
      const params = new URLSearchParams({
        domain_alias: domainAlias,
        country_code: countryCode,
        date: hiddenDate.value,
        recipient_city: citySelect.value,
        shopify_product_ids: productId
      });

      try {
        const data = await requestWithFallback(
          `${PRIMARY_API}/timeslot?${params}`,
          `${FALLBACK_API}/timeslot?${params}`
        );
        if (requestSeq !== timeslotRequestSeq) return;
        const slots = (Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])).filter(hasConfiguredSpecificCharge);
        currentSlots = slots;
        timeslotSelect.innerHTML = '<option value="">Select a time slot</option>';

        slots.forEach((slot) => {
          const option = document.createElement('option');
          option.value = String(slot.idx ?? slot.id ?? '');
          option.textContent = String(slot.text || slot.label || slot.name || 'Timeslot');
          option.dataset.text = option.textContent;
          option.dataset.specific = String(slot.is_specific_timing || 0);
          option.dataset.start = String(slot.start_time ?? '');
          option.dataset.end = String(slot.end_time ?? '');
          option.dataset.charge = String(slot.charge ?? 0);
          option.dataset.cutoffTime = String(slot.cutoff_time ?? '');
          timeslotSelect.appendChild(option);
        });

        if (!slots.length) {
          const cityName = selectedCityName();
          const dateLabel = formatDeliveryDate(hiddenDate.value);
          resetTimeslot('Choose another delivery date');
          setStatus(`No delivery times are available for ${cityName}${dateLabel ? ` on ${dateLabel}` : ''}. Please choose another delivery date.`, 'warning');
          showChangeDate(true);
          return;
        }

        timeslotSelect.disabled = false;
        showChangeDate(false);
        setStatus('Available delivery times loaded. Choose your preferred timeslot.', 'success');

        if (restore) {
          const stored = sessionStorage.getItem(SESSION.timeslot) || '';
          const matching = Array.from(timeslotSelect.options).find((option) => option.value === stored && !option.disabled);
          if (matching) {
            timeslotSelect.value = stored;
            timeslotSelect.dispatchEvent(new Event('change'));
            const storedSpecific = sessionStorage.getItem(SESSION.specific) || '';
            if (storedSpecific && !specificWrap.hidden) {
              specificSelect.value = storedSpecific;
              specificSelect.dispatchEvent(new Event('change'));
            }
          }
        }
      } catch (error) {
        if (requestSeq !== timeslotRequestSeq) return;
        console.error('[FlowerAdvisor] Timeslot API failed.', error);
        const reason = shopperSafeReason(error);
        const cityName = selectedCityName();
        const dateLabel = formatDeliveryDate(hiddenDate?.value);
        const isToday = hiddenDate?.value === localISODate();

        resetTimeslot('Choose another delivery date');
        if (reason) {
          const reasonWithPunctuation = /[.!?]$/.test(reason) ? reason : `${reason}.`;
          setStatus(`${reasonWithPunctuation} Please choose another delivery date.`, 'warning');
        } else if (isToday) {
          setStatus(`We couldn't confirm same-day delivery times for ${cityName} today. Same-day availability may already be closed. Please choose another delivery date.`, 'warning');
        } else {
          setStatus(`We couldn't confirm delivery times for ${cityName}${dateLabel ? ` on ${dateLabel}` : ''}. This date may be unavailable for this product or delivery area. Please choose another delivery date or try again.`, 'warning');
        }
        showChangeDate(true);
      }
    }

    async function loadCities({ restore = false } = {}) {
      const requestSeq = ++cityRequestSeq;
      timeslotRequestSeq += 1;
      const sku = String(skuInput?.value || '').trim();
      if (!citySelect) return;

      citySelect.disabled = true;
      citySelect.innerHTML = '<option value="">Loading cities...</option>';
      resetTimeslot();
      showChangeDate(false);

      if (!sku) {
        citySelect.innerHTML = '<option value="">Delivery cities unavailable</option>';
        setStatus('Delivery availability is unavailable for this variant. Please try another product option or try again.', 'error');
        clearLegacyDeliveryLocks();
        return;
      }

      const params = new URLSearchParams({ country_code: countryCode, product_code: sku });
      try {
        const data = await requestWithFallback(
          `${PRIMARY_API}/city?${params}`,
          `${FALLBACK_API}/city?${params}`
        );
        if (requestSeq !== cityRequestSeq) return;
        const cities = Array.isArray(data?.data) ? data.data : [];
        citySelect.innerHTML = '<option value="">Select a city</option>';

        cities.forEach((city) => {
          const option = document.createElement('option');
          option.value = cityKey(city.name);
          option.textContent = `${city.name} - ${formatMYR(city.charge)}`;
          option.dataset.cityName = city.name;
          option.dataset.charge = String(city.charge ?? 0);
          citySelect.appendChild(option);
        });

        citySelect.disabled = cities.length === 0;
        if (!cities.length) {
          citySelect.innerHTML = '<option value="">No cities available</option>';
          setStatus('This product is not currently available for delivery to the listed areas. Please try another product.', 'error');
          clearLegacyDeliveryLocks();
          return;
        }

        const values = Array.from(citySelect.options).map((option) => option.value);
        const storedCity = restore ? (sessionStorage.getItem(SESSION.city) || '') : '';
        const nextCity = values.includes(storedCity) ? storedCity : '';

        if (nextCity) {
          citySelect.value = nextCity;
          sessionStorage.setItem(SESSION.city, nextCity);
        } else {
          citySelect.value = '';
        }

        const selectedName = citySelect.options[citySelect.selectedIndex]?.dataset.cityName || '';
        if (selectedName) {
          setStatus(`${selectedName} selected. Choose a delivery date to see available times.`);
        } else {
          setStatus('Choose a delivery city and date to check available times.');
        }

        if (citySelect.value && hiddenDate?.value) loadTimeslots({ restore: true });
        clearLegacyDeliveryLocks();
      } catch (error) {
        if (requestSeq !== cityRequestSeq) return;
        console.error('[FlowerAdvisor] City API failed.', error);
        citySelect.innerHTML = '<option value="">Unable to load cities</option>';
        setStatus('Delivery cities could not be checked right now. Please refresh the page or try again shortly.', 'error');
        clearLegacyDeliveryLocks();
      }
    }

    function syncVariant({ force = false } = {}) {
      const variantId = String(variantInput?.value || '');
      if (!variantId || (!force && variantId === lastVariantId)) return;
      lastVariantId = variantId;
      const nextSku = skuMap[variantId] ?? '';
      if (skuInput && skuInput.value !== nextSku) skuInput.value = nextSku;
      sessionStorage.removeItem(SESSION.timeslot);
      sessionStorage.removeItem(SESSION.specific);
      clearCutoffTime();

      if (humanActivated) {
        loadCities({ restore: true });
      } else if (citySelect) {
        citySelect.disabled = false;
        citySelect.innerHTML = '<option value="">Select to load available cities</option>';
        resetTimeslot();
        showChangeDate(false);
        setStatus('Choose a delivery city to check availability.');
      }

      clearLegacyDeliveryLocks();
    }

    function activateDeliveryAvailability(event) {
      if (humanActivated) return;
      if (event && event.isTrusted === false) return;
      humanActivated = true;
      setStatus('Loading available delivery cities...');
      loadCities({ restore: true });
    }

    ['pointerdown', 'touchstart', 'keydown', 'focusin'].forEach((eventName) => {
      widget.addEventListener(eventName, activateDeliveryAvailability, {
        once: true,
        passive: eventName === 'pointerdown' || eventName === 'touchstart'
      });
    });

    citySelect?.addEventListener('change', () => {
      sessionStorage.setItem(SESSION.city, citySelect.value);
      sessionStorage.removeItem(SESSION.timeslot);
      sessionStorage.removeItem(SESSION.specific);
      clearCutoffTime();
      showChangeDate(false);
      if (citySelect.value && hiddenDate?.value) loadTimeslots();
      else {
        resetTimeslot();
        const selectedName = citySelect.options[citySelect.selectedIndex]?.dataset.cityName || '';
        setStatus(selectedName ? `${selectedName} selected. Choose a delivery date to see available times.` : 'Choose a delivery city and date to check available times.');
      }
    });

    datePicker?.addEventListener('change', () => {
      if (hiddenDate) hiddenDate.value = datePicker.value;
      sessionStorage.setItem(SESSION.date, datePicker.value);
      sessionStorage.removeItem(SESSION.timeslot);
      sessionStorage.removeItem(SESSION.specific);
      clearCutoffTime();
      showChangeDate(false);
      if (citySelect?.value && datePicker.value) loadTimeslots();
      else resetTimeslot();
    });

    timeslotSelect?.addEventListener('change', () => {
      const option = timeslotSelect.options[timeslotSelect.selectedIndex];
      const slot = currentSlots.find((item) => String(item.idx ?? item.id ?? '') === String(timeslotSelect.value));
      if (hiddenTimeslot) hiddenTimeslot.value = timeslotSelect.value;
      if (hiddenTimeslotText) hiddenTimeslotText.value = option?.dataset.text || option?.textContent || '';
      sessionStorage.setItem(SESSION.timeslot, timeslotSelect.value);
      setCutoffTime(slot?.cutoff_time ?? option?.dataset.cutoffTime ?? '');
      if (hiddenSpecific) hiddenSpecific.value = '';
      if (specificWrap) specificWrap.hidden = true;
      if (slot && String(slot.is_specific_timing) === '1') renderSpecificTimes(slot);
      if (timeslotSelect.value) setStatus('Delivery selection saved with this item.', 'success');
    });

    specificSelect?.addEventListener('change', () => {
      if (hiddenSpecific) hiddenSpecific.value = specificSelect.value;
      sessionStorage.setItem(SESSION.specific, specificSelect.value);
      if (specificSelect.value) setStatus('Specific delivery time saved with this item.', 'success');
    });

    changeDateButton?.addEventListener('click', () => {
      if (!datePicker) return;
      datePicker.value = '';
      if (hiddenDate) hiddenDate.value = '';
      sessionStorage.removeItem(SESSION.date);
      sessionStorage.removeItem(SESSION.timeslot);
      sessionStorage.removeItem(SESSION.specific);
      clearCutoffTime();
      resetTimeslot('Choose a date to see available times');
      showChangeDate(false);
      setStatus('Choose another delivery date to check available times.');
      datePicker.focus({ preventScroll: false });
      try { datePicker.showPicker?.(); } catch (_) {}
    });

    form.addEventListener('change', () => setTimeout(() => syncVariant(), 0));

    if (variantInput) {
      new MutationObserver(() => {
        syncVariant();
        clearLegacyDeliveryLocks();
      }).observe(variantInput, { attributes: true, attributeFilter: ['value', 'disabled'] });
    }

    const storedDate = sessionStorage.getItem(SESSION.date) || '';
    if (storedDate && datePicker && storedDate >= datePicker.min) {
      datePicker.value = storedDate;
      if (hiddenDate) hiddenDate.value = storedDate;
    } else {
      sessionStorage.removeItem(SESSION.date);
    }

    clearLegacyDeliveryLocks();
    syncVariant({ force: true });
  }

  function initAll(root = document) {
    root.querySelectorAll?.('[data-fa-delivery-widget]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  // Only inspect added subtrees containing delivery widgets; never rescan the entire page.
  if (document.querySelector('product-info, [data-fa-delivery-widget]')) {
    const scope = document.querySelector('main') || document.body;
    const observer = new MutationObserver((mutations) => {
      const added = new Set();
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.('[data-fa-delivery-widget]') || node.querySelector?.('[data-fa-delivery-widget]')) added.add(node);
        }
      }
      added.forEach(node => {
        if (node.matches?.('[data-fa-delivery-widget]')) initWidget(node);
        initAll(node);
      });
    });
    observer.observe(scope, { childList: true, subtree: true });
  }
})();
